import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Card from '../../../components/ui/Card';
import UnsavedChangesBar from '../../../components/ui/UnsavedChangesBar';
import PermissionMatrix from '../../../components/permissions/PermissionMatrix';
import { fetchPermissionCatalog } from '../../../api/roles';
import { syncUserPermissions } from '../../../api/users';
import { pushToast } from '../../../components/ui/toastBus';
import { usePermission } from '../../../auth/usePermission';
import Spinner from '../../../components/ui/Spinner';

export default function PermissionsTab({ user }) {
  const { can } = usePermission();
  const readOnly = !can('permissions.assign');
  const queryClient = useQueryClient();
  const catalogQuery = useQuery({ queryKey: ['permissions-catalog'], queryFn: fetchPermissionCatalog });

  const [direct, setDirect] = useState(user.direct_permissions);

  useEffect(() => setDirect(user.direct_permissions), [user.direct_permissions]);

  const dirty = JSON.stringify([...direct].sort()) !== JSON.stringify([...user.direct_permissions].sort());

  const mutation = useMutation({
    mutationFn: () => syncUserPermissions(user.id, direct),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Permissions updated.' });
      queryClient.invalidateQueries({ queryKey: ['users', String(user.id)] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not update permissions.' }),
  });

  if (catalogQuery.isLoading) {
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <PermissionMatrix
          mode="user"
          groups={catalogQuery.data || {}}
          rolePermissions={user.role_permissions}
          directPermissions={direct}
          onDirectChange={setDirect}
          readOnly={readOnly}
          roleNames={user.roles}
        />
      </Card>

      {!readOnly && (
        <UnsavedChangesBar
          dirty={dirty}
          saving={mutation.isPending}
          onSave={() => mutation.mutate()}
          onDiscard={() => setDirect(user.direct_permissions)}
        />
      )}
    </div>
  );
}
